import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Clock, Calendar, MessageSquare } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "-";
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

export default function Approvals() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [comment, setComment] = useState("");

  async function load() {
    try {
      setError(null);
      const { data } = await api.get("/staff/timesheet/pending");
      setItems(data);
    } catch (e) {
      setError(apiErr(e));
      setItems([]);
      toast.error(apiErr(e));
    }
  }
  useEffect(() => { load(); }, []);

  async function decide(id, action, cmt = "") {
    setBusy(id);
    try {
      await api.post(`/staff/timesheet/${id}/review`, { action, comment: cmt });
      toast.success(action === "approve" ? "Pontaj aprobat" : "Pontaj respins");
      setRejectFor(null); setComment("");
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(null); }
  }

  async function bulkApprove(entryIds) {
    let ok = 0, fail = 0;
    for (const id of entryIds) {
      try { await api.post(`/staff/timesheet/${id}/review`, { action: "approve", comment: "" }); ok++; }
      catch (_) { fail++; }
    }
    if (fail === 0) toast.success(`${ok} pontaje aprobate`);
    else toast.error(`${ok} aprobate, ${fail} au eșuat`);
    await load();
  }

  // Group by employee
  const groups = {};
  (items || []).forEach((e) => {
    const key = e.employee?.id || "unknown";
    (groups[key] = groups[key] || { employee: e.employee, entries: [] }).entries.push(e);
  });
  const groupList = Object.values(groups);

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1400px]">
        <div className="mb-10">
          <div className="text-[13px] font-display font-medium text-ink-muted mb-3">— Aprobări</div>
          <h1 className="font-display font-semibold text-[42px] md:text-[48px] tracking-[-0.02em] leading-tight text-ink">Pontaje spre aprobare</h1>
          <p className="mt-3 text-ink-muted">Analizează orele înregistrate de echipă și aprobă sau respinge cu comentariu.</p>
        </div>

        {items === null ? (
          <div className="flex items-center justify-center py-24 text-ink-muted">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Se încarcă...
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-neutral-300 bg-white">
            <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
            <p className="text-ink-muted">Niciun pontaj în așteptare. Totul e la zi.</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="approvals-list">
            {groupList.map((g) => {
              const totalHours = g.entries.reduce((s, e) => s + e.hours, 0);
              const entryIds = g.entries.map((e) => e.id);
              return (
                <div key={g.employee.id} className="bg-white border border-neutral-200 rounded-2xl md:rounded-none overflow-hidden">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 md:px-6 py-4 border-b border-neutral-100 bg-ink-soft">
                    <div className="min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{g.employee.job_title}</div>
                      <h3 className="font-display font-bold text-lg tracking-tight truncate">{g.employee.name}</h3>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6">
                      <div className="sm:text-right">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">În așteptare</div>
                        <div className="font-display font-bold text-sm sm:text-base">{g.entries.length} intrări · {totalHours.toFixed(1)} h</div>
                      </div>
                      <button
                        data-testid={`bulk-approve-${g.employee.id}`}
                        onClick={() => bulkApprove(entryIds)}
                        className="btn-brand py-2 px-4 text-sm whitespace-nowrap"
                      ><CheckCircle2 className="h-3.5 w-3.5" /> Aprobă tot ({g.entries.length})</button>
                    </div>
                  </div>
                  <div className="divide-y divide-neutral-100">
                    {g.entries.map((e) => {
                      const isReject = rejectFor === e.id;
                      const isBusy = busy === e.id;
                      return (
                        <div key={e.id} className="px-4 md:px-6 py-4" data-testid={`approval-entry-${e.id}`}>
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-6">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap">
                                <div className="flex items-center gap-2 text-sm">
                                  <Calendar className="h-4 w-4 text-ink-muted" />
                                  <span className="font-mono">{fmtDate(e.date)}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <Clock className="h-4 w-4 text-brand" />
                                  <span className="font-display font-bold text-brand">{e.hours} h</span>
                                </div>
                                <span className="hidden sm:inline text-xs text-ink-muted">·</span>
                                <span className="text-sm font-medium basis-full sm:basis-auto">{e.project?.name} <span className="font-mono text-[10px] text-ink-muted ml-1">{e.project?.code}</span></span>
                                {e.status === "pending_deletion" && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-100 text-orange-700 border border-orange-300">
                                    Cerere de ștergere
                                  </span>
                                )}
                              </div>
                              {e.description && (
                                <div className="mt-2 text-sm text-ink-muted flex items-start gap-2">
                                  <MessageSquare className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                  <span>{e.description}</span>
                                </div>
                              )}
                              {isReject && (
                                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                                  <input
                                    autoFocus
                                    data-testid={`reject-comment-${e.id}`}
                                    value={comment}
                                    onChange={(ev) => setComment(ev.target.value)}
                                    placeholder="Motivul respingerii..."
                                    className="flex-1 px-3 py-2 border border-red-300 focus:border-red-500 focus:outline-none text-sm"
                                  />
                                  <div className="flex items-center gap-2">
                                    <button
                                      data-testid={`reject-confirm-${e.id}`}
                                      disabled={isBusy}
                                      onClick={() => decide(e.id, "reject", comment)}
                                      className="flex-1 sm:flex-none px-4 py-2 bg-red-600 text-white text-sm font-semibold hover:bg-red-700"
                                    >Confirmă respingere</button>
                                    <button onClick={() => { setRejectFor(null); setComment(""); }} className="text-sm text-ink-muted hover:text-ink px-2">Renunță</button>
                                  </div>
                                </div>
                              )}
                            </div>
                            {!isReject && (
                              <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                                <button
                                  data-testid={`approve-${e.id}`}
                                  disabled={isBusy}
                                  onClick={() => decide(e.id, "approve")}
                                  className="flex-1 sm:flex-none justify-center flex items-center gap-1.5 px-3 py-1.5 border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white text-sm transition-colors"
                                ><CheckCircle2 className="h-4 w-4" /> {e.status === "pending_deletion" ? "Confirmă ștergerea" : "Aprobă"}</button>
                                <button
                                  data-testid={`reject-open-${e.id}`}
                                  disabled={isBusy}
                                  onClick={() => setRejectFor(e.id)}
                                  className="flex-1 sm:flex-none justify-center flex items-center gap-1.5 px-3 py-1.5 border border-red-300 text-red-700 hover:bg-red-600 hover:text-white text-sm transition-colors"
                                ><XCircle className="h-4 w-4" /> Respinge</button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
